import { useState, useEffect, useCallback } from 'react'
import type { CutleryType } from './CutlerySVGs'
import { shuffle } from '../utils'

export type { CutleryType }
export type Difficulty = 'easy' | 'medium' | 'hard'
export type CutleryMode = 'drawer' | 'table' | 'count'

export interface CutleryItem {
  id:   string
  type: CutleryType
}

const ALL_TYPES: CutleryType[] = ['spoon', 'fork', 'knife', 'teaspoon']

const DIFFICULTY_CONFIG: Record<Difficulty, {
  types:       CutleryType[]
  minPerType:  number
  maxPerType:  number
}> = {
  easy:   { types: ['spoon', 'fork', 'knife'],              minPerType: 2, maxPerType: 4 },
  medium: { types: ['spoon', 'fork', 'knife'],              minPerType: 3, maxPerType: 6 },
  hard:   { types: ['spoon', 'fork', 'knife', 'teaspoon'],  minPerType: 3, maxPerType: 7 },
}

export interface CutleryState {
  types:        CutleryType[]
  total:        number
  pile:         CutleryItem[]
  drawer:       Record<CutleryType, CutleryItem[]>
  hints:        Record<CutleryType, CutleryItem[]>
  selectedId:   string | null
  wrongSlot:    CutleryType | null
  moves:        number
  startTime:    number | null
  endTime:      number | null
  gameComplete: boolean
}

function emptyRecord(): Record<CutleryType, CutleryItem[]> {
  const r = {} as Record<CutleryType, CutleryItem[]>
  for (const t of ALL_TYPES) r[t] = []
  return r
}

function initState(difficulty: Difficulty): CutleryState {
  const { types, minPerType, maxPerType } = DIFFICULTY_CONFIG[difficulty]

  const pileItems: CutleryItem[] = types.flatMap((type) => {
    const count = minPerType + Math.floor(Math.random() * (maxPerType - minPerType + 1))
    return Array.from({ length: count }, (_, i) => ({ id: `pile-${type}-${i}`, type }))
  })

  // Always exactly 1 hint per active compartment
  const hints = emptyRecord()
  for (const type of types) {
    hints[type] = [{ id: `hint-${type}-0`, type }]
  }

  return {
    types,
    total:        pileItems.length,
    pile:         shuffle(pileItems),
    drawer:       emptyRecord(),
    hints,
    selectedId:   null,
    wrongSlot:    null,
    moves:        0,
    startTime:    null,
    endTime:      null,
    gameComplete: false,
  }
}

export function useCutlery(difficulty: Difficulty) {
  const [state, setState] = useState<CutleryState>(() => initState(difficulty))

  const resetGame = useCallback(() => {
    setState(initState(difficulty))
  }, [difficulty])

  // Clear wrong-slot indicator after animation completes
  useEffect(() => {
    if (!state.wrongSlot) return
    const timer = setTimeout(() => {
      setState((prev) => ({ ...prev, wrongSlot: null }))
    }, 650)
    return () => clearTimeout(timer)
  }, [state.wrongSlot])

  const selectItem = useCallback((id: string) => {
    setState((prev) => {
      if (prev.wrongSlot) return prev
      if (prev.selectedId === id) return { ...prev, selectedId: null }
      return { ...prev, selectedId: id, startTime: prev.startTime ?? Date.now() }
    })
  }, [])

  const placeInSlot = useCallback((slotType: CutleryType) => {
    setState((prev) => {
      if (!prev.selectedId || prev.wrongSlot) return prev

      const item = prev.pile.find((p) => p.id === prev.selectedId)
      if (!item) return prev

      const newMoves  = prev.moves + 1
      const startTime = prev.startTime ?? Date.now()

      if (item.type === slotType) {
        const newPile      = prev.pile.filter((p) => p.id !== prev.selectedId)
        const newDrawer    = { ...prev.drawer, [slotType]: [...prev.drawer[slotType], item] }
        const gameComplete = newPile.length === 0
        return { ...prev, pile: newPile, drawer: newDrawer, selectedId: null, moves: newMoves, startTime, gameComplete, endTime: gameComplete ? Date.now() : prev.endTime }
      }

      return { ...prev, selectedId: null, wrongSlot: slotType, moves: newMoves, startTime }
    })
  }, [])

  const placeItemById = useCallback((itemId: string, slotType: CutleryType) => {
    setState((prev) => {
      if (prev.wrongSlot) return prev

      const item = prev.pile.find((p) => p.id === itemId)
      if (!item) return prev

      const newMoves  = prev.moves + 1
      const startTime = prev.startTime ?? Date.now()

      if (item.type === slotType) {
        const newPile      = prev.pile.filter((p) => p.id !== itemId)
        const newDrawer    = { ...prev.drawer, [slotType]: [...prev.drawer[slotType], item] }
        const gameComplete = newPile.length === 0
        return { ...prev, pile: newPile, drawer: newDrawer, selectedId: null, moves: newMoves, startTime, gameComplete, endTime: gameComplete ? Date.now() : prev.endTime }
      }

      return { ...prev, selectedId: null, wrongSlot: slotType, moves: newMoves, startTime }
    })
  }, [])

  return { state, resetGame, selectItem, placeInSlot, placeItemById }
}
